import { YandexTaskInfo, YandexTaskParent } from '@/types/yandex';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { DonutData } from './types';

export const ProjectDistributionMode = {
  project: 'project',
  parent: 'parent',
  epic: 'epic',
} as const;

export const NO_EPIC_LABEL = 'Без эпика';
export const NO_PARENT_LABEL = 'Без род. задачи';
export const OTHER_DISTRIBUTION_LABEL = 'Другое';
export const TOP_DISTRIBUTION_CHART_LIMIT = 10;

const NO_GROUP_KEY = '__no_group__';
export const OTHER_DISTRIBUTION_KEY = '__other__';

type LogWithMinutes = {
  taskKey: string;
  durationMinutes: number;
};

type GroupInfo = {
  key: string;
  display: string;
  minutes: number;
  trackerKey?: string;
};

export function takeTopWithOther<T extends { minutes: number }>(
  sortedItems: T[],
  limit = TOP_DISTRIBUTION_CHART_LIMIT,
): { items: T[]; otherMinutes: number } {
  const items = sortedItems.slice(0, limit);
  const otherMinutes = sortedItems.slice(limit).reduce((sum, item) => sum + item.minutes, 0);

  return { items, otherMinutes };
}

export function buildDonutChartDataFromMinutesMap(
  minutesMap: Record<string, number>,
  getDisplayName: (key: string) => string = (key) => key,
  getTaskKey: (key: string) => string | undefined = () => undefined,
): DonutData[] {
  const sorted = Object.entries(minutesMap).sort((a, b) => b[1] - a[1]);
  const totalMinutes = sorted.reduce((sum, [, minutes]) => sum + minutes, 0);

  if (totalMinutes === 0) {
    return [];
  }

  return sorted.map(([key, minutes]) => ({
    name: getDisplayName(key),
    taskKey: getTaskKey(key),
    value: minutes,
    label: formatDurationRussian(minutes),
    percent: ((minutes / totalMinutes) * 100).toFixed(1) + '%',
    colorKey: key,
  }));
}

export function buildProjectChartDataFromLogs(logs: LogWithMinutes[]): DonutData[] {
  if (logs.length === 0) {
    return [];
  }

  const projectMap: Record<string, number> = {};

  logs.forEach((log) => {
    const projectPrefix = log.taskKey.split('-')[0];
    projectMap[projectPrefix] = (projectMap[projectPrefix] || 0) + log.durationMinutes;
  });

  return buildDonutChartDataFromMinutesMap(projectMap);
}

function resolveGroupFromEntity(
  entity: YandexTaskParent | undefined,
  fallbackLabel: string,
): Pick<GroupInfo, 'key' | 'display' | 'trackerKey'> {
  if (!entity) {
    return { key: NO_GROUP_KEY, display: fallbackLabel };
  }

  return {
    key: entity.key || entity.id,
    display: entity.display || entity.key,
    trackerKey: entity.key,
  };
}

function buildGroupedChartDataFromLogs(
  logs: LogWithMinutes[],
  getTaskInfoByKey: (taskKey: string) => YandexTaskInfo | undefined,
  resolveGroup: (
    task: YandexTaskInfo | undefined,
  ) => Pick<GroupInfo, 'key' | 'display' | 'trackerKey'>,
): DonutData[] {
  if (logs.length === 0) {
    return [];
  }

  const groupMap = new Map<string, GroupInfo>();

  logs.forEach((log) => {
    const task = getTaskInfoByKey(log.taskKey);
    const { key, display, trackerKey } = resolveGroup(task);
    const existing = groupMap.get(key);

    if (existing) {
      existing.minutes += log.durationMinutes;

      return;
    }

    groupMap.set(key, {
      key,
      display,
      minutes: log.durationMinutes,
      trackerKey,
    });
  });

  const sortedGroups = [...groupMap.values()].sort((a, b) => b.minutes - a.minutes);
  const { items, otherMinutes } = takeTopWithOther(sortedGroups);

  const minutesMap: Record<string, number> = {};
  const displayByKey: Record<string, string> = {};
  const trackerKeyByKey: Record<string, string> = {};

  items.forEach((group) => {
    minutesMap[group.key] = group.minutes;
    displayByKey[group.key] = group.display;

    if (group.trackerKey) {
      trackerKeyByKey[group.key] = group.trackerKey;
    }
  });

  if (otherMinutes > 0) {
    minutesMap[OTHER_DISTRIBUTION_KEY] = otherMinutes;
    displayByKey[OTHER_DISTRIBUTION_KEY] = OTHER_DISTRIBUTION_LABEL;
  }

  return buildDonutChartDataFromMinutesMap(
    minutesMap,
    (key) => displayByKey[key] ?? key,
    (key) => trackerKeyByKey[key],
  );
}

export function buildParentProjectChartDataFromLogs(
  logs: LogWithMinutes[],
  getTaskInfoByKey: (taskKey: string) => YandexTaskInfo | undefined,
): DonutData[] {
  return buildGroupedChartDataFromLogs(logs, getTaskInfoByKey, (task) =>
    resolveGroupFromEntity(task?.parent, NO_PARENT_LABEL),
  );
}

export function buildEpicFieldProjectChartDataFromLogs(
  logs: LogWithMinutes[],
  getTaskInfoByKey: (taskKey: string) => YandexTaskInfo | undefined,
): DonutData[] {
  return buildGroupedChartDataFromLogs(logs, getTaskInfoByKey, (task) =>
    resolveGroupFromEntity(task?.epic, NO_EPIC_LABEL),
  );
}
